import identity from 'lodash/identity';
import isNil from 'lodash/isNil';
import moment from 'moment';

import {SORT_ORDERING} from 'common/util/array';
import {Cache} from 'common/util/cache';
import {OTHER_SEGMENT_VALUE} from 'common/report/util/insights/constants';

// "123456"
export function parseNumber(val) {
  if (Number.isFinite(val)) {
    return val;
  } else if (!val) {
    return null;
  } else {
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
  }
}

function parseScientificNumber(numStr) {
  const val = Number(numStr);
  if (!Number.isNaN(val)) {
    return val;
  }
  const unit = numStr.charAt(numStr.length - 1);
  const magnitude = Number(numStr.substring(0, numStr.length - 1));
  const multiplier = {
    K: 10 ** 3,
    M: 10 ** 6,
    B: 10 ** 9,
    T: 10 ** 12,
    P: 10 ** 15,
  }[unit];
  return multiplier ? magnitude * multiplier : null;
}

export function parseNumberRange(val) {
  const numberRegex = `-?\\d+\\.?\\d*[K|M|B|T|P]{0,1}`; // matches 3, -3, 3.14, 3.14K, 3.14M

  // matches < 3 times, < 3
  const lowerMatch = val.match(new RegExp(`^< (${numberRegex})(?: times?)?$`));
  if (lowerMatch) {
    return [null, parseScientificNumber(lowerMatch[1])];
  }

  // matches >= 3 times, >= 3
  const higherMatch = val.match(new RegExp(`^>= (${numberRegex})(?: times?)?$`));
  if (higherMatch) {
    return [parseScientificNumber(higherMatch[1]), null];
  }

  // matches 1 - 2 times, 1 time, 2 times
  const rangeMatches = val.match(new RegExp(`^(${numberRegex})(?: - (${numberRegex}))?(?: times?)?$`));
  if (rangeMatches) {
    return [parseScientificNumber(rangeMatches[1]), rangeMatches[2] ? parseScientificNumber(rangeMatches[2]) : null];
  }

  return null;
}

export function parseNumberRangeSortKey(val, {sortOrder = 1, ignoreNonNumeric = false} = {}) {
  const range = parseNumberRange(val);
  if (!range) {
    if (ignoreNonNumeric) {
      return null;
    }

    // Ensures non-numeric values (e.g. "undefined") are at the end regardless of sort order
    return sortOrder < 0 ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  } else if (!Number.isFinite(range[0]) && Number.isFinite(range[1])) {
    // For "< [number]", return `number - 1`
    return range[1] - 1;
  } else {
    return range[0];
  }
}

function toDate(momentObj) {
  return momentObj.isValid() ? momentObj.toDate() : null;
}

// The following date parser are based on the date formats defined at
// https://github.com/mixpanel/analytics/blob/2a9dcdaa339be3de5d4df8ef347f08004d486488/api/version_2_0/insights/util.py#L13

// "2018-07-02T13:00:00-07:00"
export function parseDateIso(val) {
  return toDate(moment.utc(val, moment.ISO_8601, true));
}

// NOTE: This format is what's output from (new Date()).toString()
// "Fri Apr 06 2018 00:08:28 GMT+0000 (UTC)"
export function parseDateTypecastString(val) {
  return toDate(moment.utc(val, `ddd MMM DD YYYY HH:mm:ss [GMT+0000 (UTC)]`, true));
}

// "1am, Fri, Apr 06"
export function parseDateHourly(val) {
  // Ideally we would parse this with "ha, ddd, MMM DD". However, moment requires the day of week to match the
  // month/day for the current year since year is unspecified (see moment's weekdayMismatch flag). So, instead,
  // we have to use this more convoluted way to match the date.
  const parts = val.split(`, `);
  if (parts.length !== 3) {
    return null;
  }
  if (!moment(parts[1], `ddd`, true).isValid()) {
    return false;
  }
  return toDate(moment.utc([parts[0], parts[2]].join(`, `), `ha, MMM DD`, true));
}

// "Jul 02 '18"
export function parseDateDaily(val) {
  return toDate(moment.utc(val, `MMM DD 'YY`, true));
}

// "Apr 02 - Apr 08 '18"
export function parseDateWeekly(val) {
  const dateStrs = val.split(` - `);
  if (dateStrs.length !== 2) {
    return null;
  }
  const dates = [toDate(moment.utc(dateStrs[0], `MMM DD`, true)), toDate(moment.utc(dateStrs[1], `MMM DD 'YY`, true))];
  return dates.every((date) => !!date) ? dates[1] : null;
}

// "Apr 2018"
export function parseDateMonthly(val) {
  return toDate(moment.utc(val, `MMM YYYY`, true));
}

// "Q1 2018"
export function parseDateQuarterly(val) {
  return toDate(moment.utc(val, `[Q]Q YYYY`, true));
}

export function parseString(val) {
  return `${val}`.toLowerCase();
}

export const HEADER_TYPES = {
  DATE_ISO: `date_iso`,
  DATE_TYPECAST_STRING: `date_typecast_string`,
  DATE_HOURLY: `date_hourly`,
  DATE_DAILY: `date_daily`,
  DATE_WEEKLY: `date_weekly`,
  DATE_MONTHLY: `date_monthly`,
  DATE_QUARTERLY: `date_quarterly`,
  NUMBER: `number`,
  NUMBER_RANGE: `number_range`,
  STRING: `string`,
};

const HEADER_TYPE_TO_PARSER = {
  [HEADER_TYPES.DATE_ISO]: parseDateIso,
  [HEADER_TYPES.DATE_TYPECAST_STRING]: parseDateTypecastString,
  [HEADER_TYPES.DATE_HOURLY]: parseDateHourly,
  [HEADER_TYPES.DATE_DAILY]: parseDateDaily,
  [HEADER_TYPES.DATE_WEEKLY]: parseDateWeekly,
  [HEADER_TYPES.DATE_MONTHLY]: parseDateMonthly,
  [HEADER_TYPES.DATE_QUARTERLY]: parseDateQuarterly,
  [HEADER_TYPES.NUMBER]: parseNumber,
  [HEADER_TYPES.NUMBER_RANGE]: (val) => parseNumberRangeSortKey(val, {ignoreNonNumeric: true}),
  [HEADER_TYPES.STRING]: parseString,
};

const DATE_HEADER_TYPES = [
  HEADER_TYPES.DATE_ISO,
  HEADER_TYPES.DATE_TYPECAST_STRING,
  HEADER_TYPES.DATE_HOURLY,
  HEADER_TYPES.DATE_DAILY,
  HEADER_TYPES.DATE_WEEKLY,
  HEADER_TYPES.DATE_MONTHLY,
  HEADER_TYPES.DATE_QUARTERLY,
];

const HEADER_TYPE_PARSE_ORDER = [
  HEADER_TYPES.NUMBER,
  HEADER_TYPES.NUMBER_RANGE,
  ...DATE_HEADER_TYPES,
  HEADER_TYPES.STRING,
];

const HEADER_TYPE_PARSE_CACHES = {};

export function parseHeaderType({headerType, value}) {
  if (!Object.prototype.hasOwnProperty.call(HEADER_TYPE_PARSE_CACHES, headerType)) {
    HEADER_TYPE_PARSE_CACHES[headerType] = new Cache();
  }
  const cache = HEADER_TYPE_PARSE_CACHES[headerType];
  return cache.fetch(value, () => {
    return HEADER_TYPE_TO_PARSER[headerType](value);
  });
}

export function getHeaderType(value) {
  const SPECIAL_VALUES = [`undefined`, OTHER_SEGMENT_VALUE];
  if (SPECIAL_VALUES.includes(value)) {
    return null;
  }

  return HEADER_TYPE_PARSE_ORDER.find((headerType) => {
    return !isNil(parseHeaderType({headerType, value}));
  });
}

export function typedComparator({sortType, order, transform = identity} = {}) {
  if (DATE_HEADER_TYPES.includes(sortType)) {
    // This function is used for Insights reports. For legacy reason, the sort order for date properties is inverted,
    // so we gotta invert it again to obtain the actual ordering.
    order = order === `asc` ? `desc` : `asc`;
  }

  return (segment1, segment2) => {
    if (segment1.label === OTHER_SEGMENT_VALUE) {
      return 1;
    } else if (segment2.label === OTHER_SEGMENT_VALUE) {
      return -1;
    }
    const args = [segment1, segment2].map((item) => parseHeaderType({headerType: sortType, value: transform(item)}));

    /**
     * Sorting comparisons are performed with less or equal than (<=), dates need to be compared
     * with >= to generate the correct ordering (Jan 5 '20 comes first than Jan 10 '20)
     * to avoid creating a new comparator, we switch the order of comparisons
     */
    const isDateHeader = DATE_HEADER_TYPES.includes(sortType);

    return SORT_ORDERING[order].apply(null, isDateHeader ? args.reverse() : args);
  };
}
